<div class="text-fg w-full max-w-none p-6">
  <!-- Loading -->
  @if (loading()) {
    <div class="flex items-center justify-center gap-2 p-12 text-sm text-muted">
      <ui-spinner size="sm" />
      <span>Loading webhook event...</span>
    </div>
  } @else if (error() && !event()) {
    <div class="p-8 text-center text-danger">
      <p>{{ error() }}</p>
      <button type="button" uiButton variant="outline" class="mt-3" (click)="load()">Retry</button>
    </div>
  } @else if (event(); as ev) {
    <!-- Header -->
    <header class="mb-6">
      <button type="button" class="mb-3 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm text-muted hover:text-primary" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="m-0 flex items-center gap-2 font-heading text-2xl font-bold text-fg">
            <ng-icon name="lucideBell" size="22" />
            Webhook Event
          </h1>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <ui-badge [variant]="getStatusVariant(ev.status)">{{ ev.status }}</ui-badge>
            <ui-badge [variant]="getEventTypeVariant(ev.event_type)">{{ ev.event_type }}</ui-badge>
            <ui-badge class="font-mono">{{ ev.provider_code }}</ui-badge>
          </div>
        </div>
        <div class="flex gap-2">
          @if (canResend()) {
            <button type="button" uiButton [disabled]="resending()" (click)="resend()" title="Resend webhook to merchant">
              @if (resending()) {
                <ui-spinner size="sm" />
              } @else {
                <ng-icon name="lucideSend" size="14" />
              }
              <span>Resend to Merchant</span>
            </button>
          }
          @if (canRetry()) {
            <button type="button" uiButton class="bg-warning! hover:opacity-90" [disabled]="retrying()" (click)="retry()" title="Retry full processing">
              @if (retrying()) {
                <ui-spinner size="sm" />
              } @else {
                <ng-icon name="lucideRefreshCw" size="14" />
              }
              <span>Retry</span>
            </button>
          }
          <button type="button" uiButton variant="outline" aria-label="Refresh" (click)="load()" title="Refresh">
            <ng-icon name="lucideRefreshCw" size="16" />
          </button>
        </div>
      </div>
    </header>

    <!-- Error Banner -->
    @if (error()) {
      <ui-alert variant="danger">
        <div class="flex items-center justify-between gap-2">
          <span>{{ error() }}</span>
          <button type="button" class="cursor-pointer border-0 bg-transparent p-0.5 text-current hover:opacity-70" (click)="error.set(null)">
            <ng-icon name="lucideX" size="14" />
          </button>
        </div>
      </ui-alert>
    }

    <!-- ─── Event Info ──────────────── -->
    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 flex items-center gap-1.5 text-sm font-semibold text-fg-secondary">
        <ng-icon name="lucideBell" size="16" />
        Event Details
      </h3>
      <div class="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
        <div class="flex flex-col gap-0.5 md:col-span-2">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Event ID</span>
          <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm text-fg hover:text-primary" (click)="copyToClipboard(ev.id)" title="Copy">
            <code class="rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ ev.id }}</code>
            <ng-icon name="lucideClipboard" size="12" />
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Status</span>
          <ui-badge [variant]="getStatusVariant(ev.status)">{{ ev.status }}</ui-badge>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Event Type</span>
          <ui-badge [variant]="getEventTypeVariant(ev.event_type)">{{ ev.event_type }}</ui-badge>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Provider Code</span>
          <span class="break-all text-sm text-fg">{{ ev.provider_code }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Created</span>
          <span class="break-all text-sm text-fg">{{ formatDate(ev.created_at) }}</span>
        </div>
        @if (ev.transaction_id) {
          <div class="flex flex-col gap-0.5 md:col-span-2">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Transaction ID</span>
            <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm text-fg hover:text-primary" (click)="copyToClipboard(ev.transaction_id)" title="Copy">
              <code class="rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ ev.transaction_id }}</code>
              <ng-icon name="lucideClipboard" size="12" />
            </span>
          </div>
        }
        @if (ev.provider_transaction_id) {
          <div class="flex flex-col gap-0.5 md:col-span-2">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Provider Transaction ID</span>
            <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm text-fg hover:text-primary" (click)="copyToClipboard(ev.provider_transaction_id)" title="Copy">
              <code class="rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ ev.provider_transaction_id }}</code>
              <ng-icon name="lucideClipboard" size="12" />
            </span>
          </div>
        }
        @if (ev.status_message) {
          <div class="flex flex-col gap-0.5 md:col-span-2">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Status Message</span>
            <span class="break-all text-sm italic text-muted">{{ ev.status_message }}</span>
          </div>
        }
      </div>
    </section>

    <!-- ─── Related Links ──────────── -->
    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 flex items-center gap-1.5 text-sm font-semibold text-fg-secondary">
        <ng-icon name="lucideLink" size="16" />
        Related Resources
      </h3>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
        @if (ev.financial_entity) {
          <a [routerLink]="shellRoutes.commands('manage', 'entities', ev.financial_entity)" class="relative flex items-center gap-2.5 rounded-lg border border-border p-3 text-inherit no-underline transition-colors hover:border-primary hover:shadow-sm">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-fg-secondary"><ng-icon name="lucideGlobe" size="18" /></div>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wide text-muted">Financial Entity</span>
              <span class="truncate text-sm font-semibold text-fg">{{ ev.financial_entity_code ?? shortId(ev.financial_entity) }}</span>
              @if (ev.financial_entity_name) {
                <span class="truncate text-xs text-muted">{{ ev.financial_entity_name }}</span>
              }
            </div>
            <ng-icon name="lucideExternalLink" size="14" class="shrink-0 text-muted" />
          </a>
        }
        @if (ev.execution_provider) {
          <a [routerLink]="shellRoutes.commands('manage', 'providers')" class="relative flex items-center gap-2.5 rounded-lg border border-border p-3 text-inherit no-underline transition-colors hover:border-primary hover:shadow-sm">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-fg-secondary"><ng-icon name="lucideServer" size="18" /></div>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wide text-muted">Execution Provider</span>
              <span class="truncate text-sm font-semibold text-fg">{{ ev.execution_provider_code ?? shortId(ev.execution_provider) }}</span>
              @if (ev.execution_provider_name) {
                <span class="truncate text-xs text-muted">{{ ev.execution_provider_name }}</span>
              }
            </div>
            <ng-icon name="lucideExternalLink" size="14" class="shrink-0 text-muted" />
          </a>
        }
        @if (ev.merchant_id) {
          <a [routerLink]="shellRoutes.commands('manage', 'merchants', ev.merchant_id)" class="relative flex items-center gap-2.5 rounded-lg border border-border p-3 text-inherit no-underline transition-colors hover:border-primary hover:shadow-sm">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-fg-secondary"><ng-icon name="lucideStore" size="18" /></div>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wide text-muted">Merchant</span>
              <span class="truncate text-sm font-semibold text-fg">{{ ev.merchant_name ?? shortId(ev.merchant_id) }}</span>
            </div>
            <ng-icon name="lucideExternalLink" size="14" class="shrink-0 text-muted" />
          </a>
        }
        @if (ev.application) {
          <a [routerLink]="shellRoutes.commands('manage', 'merchant-applications', ev.application)" class="relative flex items-center gap-2.5 rounded-lg border border-border p-3 text-inherit no-underline transition-colors hover:border-primary hover:shadow-sm">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-fg-secondary"><ng-icon name="lucideServer" size="18" /></div>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wide text-muted">Application</span>
              <span class="truncate text-sm font-semibold text-fg">{{ ev.application_name ?? shortId(ev.application) }}</span>
              @if (ev.application_client_id) {
                <span class="truncate text-xs text-muted">{{ ev.application_client_id }}</span>
              }
            </div>
            <ng-icon name="lucideExternalLink" size="14" class="shrink-0 text-muted" />
          </a>
        }
        @if (ev.transaction_id) {
          <a [routerLink]="shellRoutes.commands('manage', getTransactionRouteKey(ev.event_type), ev.real_transaction_id ?? '')" class="relative flex items-center gap-2.5 rounded-lg border border-border p-3 text-inherit no-underline transition-colors hover:border-primary hover:shadow-sm">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-fg-secondary"><ng-icon name="lucideCodeXml" size="18" /></div>
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wide text-muted">{{ ev.event_type | titlecase }}</span>
              <span class="truncate text-sm font-semibold text-fg">{{ shortId(ev.real_transaction_id ?? null) }}</span>
            </div>
            <ng-icon name="lucideExternalLink" size="14" class="shrink-0 text-muted" />
          </a>
        }
      </div>
    </section>

    <!-- ─── Forwarding Info ─────────── -->
    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 flex items-center gap-1.5 text-sm font-semibold text-fg-secondary">
        <ng-icon name="lucideGlobe" size="16" />
        Forwarding
      </h3>
      <div class="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Attempts</span>
          <span class="break-all text-sm text-fg">{{ ev.forward_attempts }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Response Code</span>
          <span class="break-all text-sm text-fg" [class.text-success-fg]="ev.forward_response_code && ev.forward_response_code >= 200 && ev.forward_response_code < 300" [class.text-danger-fg]="ev.forward_response_code && ev.forward_response_code >= 400" [class.font-semibold]="ev.forward_response_code && (ev.forward_response_code < 300 || ev.forward_response_code >= 400)">
            {{ ev.forward_response_code ?? '—' }}
          </span>
        </div>
        @if (ev.forwarded_to_url) {
          <div class="flex flex-col gap-0.5 md:col-span-2">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Forwarded To</span>
            <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm text-fg hover:text-primary" (click)="copyToClipboard(ev.forwarded_to_url)" title="Copy">
              <code class="break-all rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ ev.forwarded_to_url }}</code>
              <ng-icon name="lucideClipboard" size="12" />
            </span>
          </div>
        }
        @if (ev.forwarded_at) {
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Forwarded At</span>
            <span class="break-all text-sm text-fg">{{ formatDate(ev.forwarded_at) }}</span>
          </div>
        }
        <div class="flex flex-col gap-0.5 md:col-span-2">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Matched Rules</span>
          <span class="break-all text-sm text-fg">
            @if (ev.matched_rules.length) {
              {{ ev.matched_rules.length }} rule(s)
            } @else {
              —
            }
          </span>
        </div>
      </div>

      @if (ev.matched_rules.length) {
        <div class="mt-3 overflow-hidden rounded-md border border-border">
          <div class="flex w-full items-center gap-1.5 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary">
            <ng-icon name="lucideLink" size="14" />
            <span>Matched Rules Used</span>
          </div>
          <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
            @for (rule of ev.matched_rules; track rule.id) {
              <div class="flex flex-col gap-0.5 md:col-span-2">
                <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">{{ rule.name }} (priority {{ rule.priority }})</span>
                <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm text-fg hover:text-primary" (click)="copyToClipboard(rule.webhook_url)" title="Copy">
                  <code class="break-all rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ rule.webhook_url }}</code>
                  <ng-icon name="lucideClipboard" size="12" />
                </span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Forward request body -->
      @if (ev.forward_request_body) {
        <div class="mt-3 overflow-hidden rounded-md border border-border">
          <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('forwardRequest')">
            <ng-icon name="lucideCodeXml" size="14" />
            <span>Forward Request Body</span>
            <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showForwardRequest()">&#9662;</span>
          </button>
          @if (showForwardRequest()) {
            <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ formatJson(ev.forward_request_body) }}</pre>
          }
        </div>
      }
      @if (ev.forward_request_headers) {
        <div class="mt-3 overflow-hidden rounded-md border border-border">
          <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('forwardRequestHeaders')">
            <ng-icon name="lucideCodeXml" size="14" />
            <span>Forward Request Headers</span>
            <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showForwardRequestHeaders()">&#9662;</span>
          </button>
          @if (showForwardRequestHeaders()) {
            <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ formatJson(ev.forward_request_headers) }}</pre>
          }
        </div>
      }
      <!-- Forward response body -->
      @if (ev.forward_response_body) {
        <div class="mt-3 overflow-hidden rounded-md border border-border">
          <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('forwardResponse')">
            <ng-icon name="lucideCodeXml" size="14" />
            <span>Forward Response Body</span>
            <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showForwardResponse()">&#9662;</span>
          </button>
          @if (showForwardResponse()) {
            <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ ev.forward_response_body }}</pre>
          }
        </div>
      }
    </section>

    <!-- ─── Request Payload ─────────── -->
    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 flex items-center gap-1.5 text-sm font-semibold text-fg-secondary">
        <ng-icon name="lucideCodeXml" size="16" />
        Request Data
      </h3>

      <!-- Payload -->
      <div class="mt-3 overflow-hidden rounded-md border border-border">
        <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('payload')">
          <ng-icon name="lucideCodeXml" size="14" />
          <span>Payload</span>
          <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showPayload()">&#9662;</span>
        </button>
        @if (showPayload()) {
          <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ formatJson(ev.payload) }}</pre>
        }
      </div>

      <!-- Headers -->
      <div class="mt-3 overflow-hidden rounded-md border border-border">
        <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('headers')">
          <ng-icon name="lucideCodeXml" size="14" />
          <span>Request Headers</span>
          <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showHeaders()">&#9662;</span>
        </button>
        @if (showHeaders()) {
          <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ formatJson(ev.headers) }}</pre>
        }
      </div>

      <!-- Confirmation Response -->
      @if (ev.confirmation_response) {
        <div class="mt-3 overflow-hidden rounded-md border border-border">
          <button type="button" class="flex w-full cursor-pointer items-center gap-1.5 border-0 bg-surface-muted px-3 py-2 text-left text-sm font-medium text-fg-secondary hover:bg-surface-container" (click)="toggleSection('confirmation')">
            <ng-icon name="lucideCheck" size="14" />
            <span>Confirmation Response (returned to provider)</span>
            <span class="ml-auto text-[0.7rem] transition-transform" [class.rotate-180]="showConfirmation()">&#9662;</span>
          </button>
          @if (showConfirmation()) {
            <pre class="m-0 max-h-100 overflow-auto whitespace-pre-wrap break-all bg-surface-container px-4 py-3 font-mono text-xs leading-relaxed text-fg">{{ formatJson(ev.confirmation_response) }}</pre>
          }
        </div>
      }
    </section>

    <!-- ─── Timestamps ──────────────── -->
    <section class="mb-4 rounded-lg border border-border bg-surface-muted px-5 py-4">
      <div class="flex items-center gap-2 py-0.5 text-xs">
        <span class="min-w-15 font-medium text-muted">Created</span>
        <span class="text-fg-secondary">{{ formatDate(ev.created_at) }}</span>
      </div>
      <div class="flex items-center gap-2 py-0.5 text-xs">
        <span class="min-w-15 font-medium text-muted">Updated</span>
        <span class="text-fg-secondary">{{ formatDate(ev.updated_at) }}</span>
      </div>
    </section>
  }

  <!-- Copy toast -->
  @if (copyToast()) {
    <div class="fixed bottom-6 right-6 z-9999 rounded-md bg-fg px-3.5 py-2 text-xs font-medium text-card shadow-lg">Copied!</div>
  }
</div>
